// Buildium-style Work Order Detail (Iteration C)
//   • Header with status pill + SLA tracker + priority + assigned vendor
//   • Three-column layout: Details | Timeline (comments+audit) | AI Recommend panel
//   • Stage transitions via guarded state machine
//   • Add comment · add cost · assign vendor inline
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { workOrderApi } from '@/utils/leasingApi';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Clock, MessageSquare, DollarSign, UserCheck, ChevronRight, Sparkles,
  AlertTriangle, Wrench, CheckCircle2, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGE_PILL = {
  created:       'bg-slate-200 text-slate-700',
  assigned:      'bg-blue-100 text-blue-800',
  in_progress:   'bg-amber-100 text-amber-800',
  vendor_action: 'bg-orange-100 text-orange-800',
  inspection:    'bg-indigo-100 text-indigo-800',
  completed:     'bg-emerald-100 text-emerald-800',
  closed:        'bg-zinc-200 text-zinc-600',
  pending:       'bg-slate-200 text-slate-700',
};

const STAGES = ['created', 'assigned', 'in_progress', 'vendor_action', 'inspection', 'completed', 'closed'];

const PRIORITY_COLOR = {
  low:    'text-slate-600',
  medium: 'text-amber-700',
  high:   'text-red-700',
  urgent: 'text-red-900 font-bold',
};

const SLA_COLOR = {
  ok:       { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  warning:  { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700' },
  breached: { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700' },
};

export default function WorkOrder() {
  const { wid } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [comment, setComment] = useState('');
  const [cost, setCost] = useState({ label: '', amount_sar: '', kind: 'parts' });
  const [showCostForm, setShowCostForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, v, r] = await Promise.all([
        workOrderApi.get(wid),
        api.getVendors().catch(() => ({ data: [] })),
        workOrderApi.aiRecommend(wid).catch(() => ({ data: null })),
      ]);
      setWo(w.data);
      setVendors(v.data || []);
      setRec(r.data);
    } catch (e) {
      toast.error('Failed to load work order');
    } finally { setLoading(false); }
  }, [wid]);
  useEffect(() => { load(); }, [load]);

  const onTransition = async (to_stage) => {
    setTransitioning(true);
    try {
      await workOrderApi.transition(wid, { to_stage });
      toast.success(`Moved to ${to_stage}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Transition failed'); }
    finally { setTransitioning(false); }
  };

  const onComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await workOrderApi.comment(wid, { body: comment, visible_to_tenant: false });
      setComment('');
      toast.success('Comment added');
      load();
    } catch (e) { toast.error('Comment failed'); }
  };

  const onAssignVendor = async (vendor_id) => {
    try {
      await workOrderApi.assign(wid, { vendor_id });
      toast.success('Vendor assigned');
      load();
    } catch (e) { toast.error('Assignment failed'); }
  };

  const onAddCost = async (e) => {
    e.preventDefault();
    if (!cost.label || !cost.amount_sar) return;
    try {
      await workOrderApi.addCost(wid, { ...cost, amount_sar: Number(cost.amount_sar) });
      toast.success('Cost logged');
      setCost({ label: '', amount_sar: '', kind: 'parts' });
      setShowCostForm(false);
      load();
    } catch (e) { toast.error('Cost log failed'); }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="animate-spin h-6 w-6 text-[#00734A]" /></div></Layout>;
  if (!wo) return <Layout><div className="text-center py-20 text-[#71777E]">Work order not found</div></Layout>;

  const currentStage = wo.stage || wo.status || 'created';
  const slaInfo = wo.sla || { state: 'ok', elapsed_hours: 0, sla_hours: 72 };
  const slaStyle = SLA_COLOR[slaInfo.state] || SLA_COLOR.ok;
  const totalCost = (wo.costs || []).reduce((s, c) => s + (c.amount_sar || 0), 0);

  return (
    <Layout>
      <div className="space-y-5" data-testid="work-order-page">
        {/* Back */}
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[12px] text-[#71777E] hover:text-[#0E1320]" data-testid="wo-back">
          <ArrowLeft size={14} /> Back to maintenance
        </button>

        {/* Header */}
        <div className="rounded-xl border border-[#E6EAF0] bg-white p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">
                <Wrench size={11} /> Work order · {wo.id?.slice(0, 8)}
              </div>
              <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-[#0E1320]" data-testid="wo-title">{wo.title || wo.description?.slice(0, 80) || 'Untitled'}</h1>
              <div className="mt-2 inline-flex items-center gap-3 flex-wrap">
                <span data-testid="wo-stage-pill" className={`px-2.5 py-0.5 rounded-full text-[11.5px] font-medium reva-mono uppercase tracking-[0.1em] ${STAGE_PILL[currentStage] || ''}`}>{currentStage}</span>
                <span className={`text-[12px] uppercase reva-mono tracking-[0.14em] ${PRIORITY_COLOR[wo.priority || 'medium']}`}>{wo.priority || 'medium'} priority</span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border reva-mono ${slaStyle.bg} ${slaStyle.border} ${slaStyle.text}`}
                  data-testid="wo-sla">
                  <Clock size={10} /> SLA · {slaInfo.elapsed_hours}h / {slaInfo.sla_hours}h ({slaInfo.state})
                </span>
              </div>
            </div>

            {/* Stage transitions */}
            <div className="flex items-center gap-1.5">
              {currentStage !== 'closed' && currentStage !== 'completed' && (
                <Button onClick={() => onTransition(getNextStage(currentStage))} disabled={transitioning} size="sm" data-testid="wo-advance">
                  {transitioning ? <Loader2 className="animate-spin h-3.5 w-3.5 mr-1.5" /> : <ChevronRight size={13} className="mr-1" />}
                  Advance → {getNextStage(currentStage)}
                </Button>
              )}
              {currentStage === 'completed' && (
                <Button onClick={() => onTransition('closed')} disabled={transitioning} size="sm" variant="outline" data-testid="wo-close">
                  <CheckCircle2 size={13} className="mr-1.5" /> Close
                </Button>
              )}
            </div>
          </div>

          {/* Stage progress bar */}
          <div className="mt-5 flex items-center gap-1 overflow-x-auto pb-2" data-testid="wo-progress">
            {STAGES.map((s, i) => {
              const reached = STAGES.indexOf(currentStage) >= i;
              return (
                <React.Fragment key={s}>
                  <div className={`flex flex-col items-center gap-1 min-w-[64px] ${reached ? '' : 'opacity-40'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${reached ? 'bg-[#00734A] text-white' : 'bg-[#E6EAF0] text-[#71777E]'}`}>
                      {i + 1}
                    </div>
                    <div className="text-[9.5px] reva-mono uppercase text-[#4A5263] tracking-[0.1em] whitespace-nowrap">{s.replace(/_/g, ' ')}</div>
                  </div>
                  {i < STAGES.length - 1 && <div className={`flex-1 h-px ${reached && STAGES.indexOf(currentStage) > i ? 'bg-[#00734A]' : 'bg-[#E6EAF0]'} min-w-[16px]`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* 3-column body */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Details */}
          <div className="space-y-4">
            <div className="rounded-xl border border-[#E6EAF0] bg-white p-4">
              <h2 className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Details</h2>
              <dl className="mt-3 text-[13px] space-y-2">
                <div className="flex justify-between"><dt className="text-[#71777E]">Property</dt><dd className="text-[#0E1320] truncate ms-2">{wo.property_name || wo.property_id?.slice(0, 8)}</dd></div>
                <div className="flex justify-between"><dt className="text-[#71777E]">Unit</dt><dd className="text-[#0E1320] reva-mono">{wo.unit_number || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-[#71777E]">Vendor</dt><dd className="text-[#0E1320] truncate ms-2">{vendors.find(v => v.id === wo.vendor_id)?.name || '—'}</dd></div>
                <div className="flex justify-between"><dt className="text-[#71777E]">Created</dt><dd className="text-[#0E1320] reva-mono">{wo.created_at ? new Date(wo.created_at).toLocaleDateString() : '—'}</dd></div>
                <div className="flex justify-between border-t border-[#ECEFF3] pt-2 mt-2"><dt className="text-[#71777E]">Total cost</dt><dd className="text-[#0E1320] reva-mono font-bold">SAR {totalCost.toLocaleString()}</dd></div>
              </dl>
              {wo.description && (
                <div className="mt-3 pt-3 border-t border-[#ECEFF3]">
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Description</div>
                  <p className="mt-1 text-[12.5px] text-[#4A5263] whitespace-pre-line">{wo.description}</p>
                </div>
              )}
            </div>

            {/* Assign vendor */}
            <div className="rounded-xl border border-[#E6EAF0] bg-white p-4">
              <h2 className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Assign vendor</h2>
              <select onChange={e => e.target.value && onAssignVendor(e.target.value)} value={wo.vendor_id || ''} className="mt-2 w-full h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="wo-assign-vendor">
                <option value="">Choose vendor…</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} {v.rating ? `(${v.rating}★)` : ''}</option>)}
              </select>
            </div>

            {/* Cost log */}
            <div className="rounded-xl border border-[#E6EAF0] bg-white p-4">
              <div className="flex items-center justify-between">
                <h2 className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E]">Costs · SAR {totalCost.toLocaleString()}</h2>
                <button onClick={() => setShowCostForm(!showCostForm)} className="text-[#00734A] text-[12px] hover:underline" data-testid="wo-cost-toggle">+ Add</button>
              </div>
              {showCostForm && (
                <form onSubmit={onAddCost} className="mt-3 space-y-2">
                  <Input placeholder="Label (e.g. AC gasket)" value={cost.label} onChange={e => setCost({ ...cost, label: e.target.value })} required data-testid="wo-cost-label" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" placeholder="Amount SAR" value={cost.amount_sar} onChange={e => setCost({ ...cost, amount_sar: e.target.value })} required data-testid="wo-cost-amount" />
                    <select value={cost.kind} onChange={e => setCost({ ...cost, kind: e.target.value })} className="h-9 px-2 border border-[#E6EAF0] rounded-md text-[13px]">
                      <option value="parts">Parts</option><option value="labor">Labor</option><option value="other">Other</option>
                    </select>
                  </div>
                  <Button type="submit" size="sm" className="w-full" data-testid="wo-cost-submit">Log cost</Button>
                </form>
              )}
              {(wo.costs || []).length > 0 && (
                <ul className="mt-3 space-y-1.5 text-[12.5px]">
                  {wo.costs.map(c => (
                    <li key={c.id} className="flex justify-between border-t border-[#ECEFF3] pt-1.5">
                      <span>{c.label} <span className="text-[10px] text-[#71777E] uppercase ms-1 reva-mono">({c.kind})</span></span>
                      <span className="reva-mono">SAR {c.amount_sar.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Timeline (middle) */}
          <div className="rounded-xl border border-[#E6EAF0] bg-white p-4 lg:col-span-1">
            <h2 className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] inline-flex items-center gap-1.5"><MessageSquare size={11} /> Timeline & comments</h2>
            <form onSubmit={onComment} className="mt-3 flex gap-2">
              <input type="text" value={comment} onChange={e => setComment(e.target.value)} placeholder="Add comment…" className="flex-1 px-3 py-1.5 border border-[#E6EAF0] rounded-md text-[13px]" data-testid="wo-comment-input" />
              <Button type="submit" size="sm" disabled={!comment.trim()} data-testid="wo-comment-submit">Post</Button>
            </form>
            <ol className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="wo-timeline">
              {(wo.timeline || []).slice().reverse().map(e => (
                <li key={e.id} className="text-[12.5px] border-s-2 border-[#E6EAF0] ps-3">
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-semibold text-[#0E1320] capitalize">{e.kind.replace(/_/g, ' ')}</span>
                    <time className="text-[10.5px] text-[#71777E] reva-mono">{new Date(e.at).toLocaleString()}</time>
                  </div>
                  <div className="mt-0.5 text-[12px] text-[#4A5263]">
                    {e.kind === 'comment' ? e.payload?.body
                      : e.kind === 'status_changed' ? `${e.payload?.from} → ${e.payload?.to}${e.payload?.note ? ` · ${e.payload.note}` : ''}`
                      : e.kind === 'cost_added' ? `${e.payload?.label} · SAR ${e.payload?.amount_sar}`
                      : e.kind === 'assigned' ? `Assigned vendor ${e.payload?.vendor_id?.slice(0, 8) || '—'}`
                      : JSON.stringify(e.payload)}
                  </div>
                  {e.actor_email && <div className="mt-0.5 text-[10.5px] text-[#71777E] reva-mono">{e.actor_email}</div>}
                </li>
              ))}
              {(!wo.timeline || wo.timeline.length === 0) && <li className="text-[12px] text-[#71777E] italic">No events yet.</li>}
            </ol>
          </div>

          {/* AI Recommend panel (right) */}
          <div className="rounded-xl border border-[#E6EAF0] bg-gradient-to-br from-[#FBFCFD] via-[#F4F9F7] to-[#E8F4EE] p-4" data-testid="wo-ai-panel">
            <h2 className="text-[10.5px] reva-mono uppercase tracking-[0.16em] text-[#71777E] inline-flex items-center gap-1.5">
              <Sparkles size={11} className="text-[#00734A]" /> AI Recommendations
            </h2>
            {rec ? (
              <div className="mt-3 space-y-3.5">
                <div className="text-[12px]">
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">Similar issues observed</div>
                  <div className="text-2xl font-bold tabular-nums text-[#0E1320]">{rec.similar_issues_count}</div>
                </div>
                <div>
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">Likely root causes</div>
                  <ul className="mt-1 text-[12.5px] text-[#0E1320] space-y-1">
                    {rec.root_cause_hypotheses.map((c) => (
                      <li key={`rc-${c}`} className="inline-flex items-start gap-1.5"><span className="text-[#00734A] mt-1">▸</span> {c}</li>
                    ))}
                  </ul>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div>
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">Estimated cost</div>
                    <div className="font-semibold text-[#0E1320]">{rec.estimated_cost_range_sar}</div>
                  </div>
                  <div>
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">Resolution</div>
                    <div className="font-semibold text-[#0E1320]">{rec.expected_resolution_window}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E] inline-flex items-center gap-1.5">
                    {rec.escalation_risk === 'high' ? <AlertTriangle size={10} className="text-red-600" /> : null}
                    Escalation risk
                  </div>
                  <div className={`font-semibold ${rec.escalation_risk === 'high' ? 'text-red-700' : rec.escalation_risk === 'medium' ? 'text-amber-700' : 'text-emerald-700'}`}>
                    {rec.escalation_risk.toUpperCase()}
                  </div>
                </div>
                {rec.suggested_vendors?.length > 0 && (
                  <div>
                    <div className="text-[10.5px] reva-mono uppercase tracking-[0.14em] text-[#71777E]">Suggested vendors</div>
                    <ul className="mt-1 space-y-1.5">
                      {rec.suggested_vendors.map(v => (
                        <li key={v.id} className="flex justify-between items-center text-[12.5px]">
                          <span>{v.name}</span>
                          <button onClick={() => onAssignVendor(v.id)} className="text-[#00734A] hover:underline text-[11.5px] inline-flex items-center gap-1" data-testid={`wo-ai-assign-${v.id}`}>
                            <UserCheck size={10} /> Assign
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-[10px] reva-mono text-[#71777E] uppercase tracking-[0.14em] border-t border-white/40 pt-2">
                  Confidence {rec.confidence_pct}% · based on {rec.similar_issues_count} similar work orders in this org
                </div>
              </div>
            ) : (
              <div className="mt-3 text-[12.5px] text-[#71777E]">AI recommendations unavailable.</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function getNextStage(current) {
  const map = {
    created: 'assigned', assigned: 'in_progress', in_progress: 'vendor_action',
    vendor_action: 'inspection', inspection: 'completed', completed: 'closed',
  };
  return map[current] || 'in_progress';
}
